import React from "react";
import { CheckboxDescription, SettingsInputItem } from "../../Common/Components/FormComponents";
import CheckBox from "../../../components/pages/CourseAuthor/Checkbox";
import { FormContainer } from "../../../components/pages/CourseAuthor/FormElements";
import { ClientFeature, parseClientFeatures } from "../../../parsers/login";
import { UserAccountPatchResponse } from "../types";
import { identity } from "ramda";

export interface AccountFeatureAccessProps {
  clientFeatures: ClientFeature[] | undefined;
  setClientFeatures: React.Dispatch<React.SetStateAction<ClientFeature[] | undefined>>;
  setModalMessage: (modalMsg: string) => void;
  insertClientFeature: (newClientFeature: ClientFeature) => Promise<UserAccountPatchResponse>;
  deleteClientFeature: (clientFeature: ClientFeature) => Promise<UserAccountPatchResponse>;
}

interface AccountFeatureCheckboxProps {
  customChecked: boolean;
  description: string;
  name: ClientFeature;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

enum Action {
  addFeature = "add",
  removeFeature = "remove",
}

const updateClientFeatures = (
  currentClientFeatures: ClientFeature[],
  featureName: ClientFeature,
  action: Action
): ClientFeature[] => {
  const idx = currentClientFeatures?.indexOf(featureName);
  const updatedClientFeatures = currentClientFeatures?.map(identity);

  if (action === Action.addFeature) {
    updatedClientFeatures?.push(featureName);
  } else {
    updatedClientFeatures?.splice(idx as number, 1);
  }

  return updatedClientFeatures;
};

const AccountFeatureCheckbox: React.FC<AccountFeatureCheckboxProps> = ({
  customChecked,
  name,
  onChange,
  description,
}) => (
  <SettingsInputItem>
    <label>
      <CheckBox customChecked={customChecked} name={name} onChange={onChange} />
      <CheckboxDescription>{description}</CheckboxDescription>
    </label>
  </SettingsInputItem>
);
const AccountFeatureAccess: React.FC<AccountFeatureAccessProps> = ({
  clientFeatures,
  setClientFeatures,
  setModalMessage,
  insertClientFeature,
  deleteClientFeature,
}) => {
  const accessPermissions = parseClientFeatures(clientFeatures as string[]);
  const checkboxDescriptions = [
    { name: ClientFeature.author, desc: "Author", customChecked: accessPermissions.isAuthor },
    { name: ClientFeature.manager, desc: "Manager", customChecked: accessPermissions.isManager },
    { name: ClientFeature.admin, desc: "Admin", customChecked: accessPermissions.isAdmin },
    { name: ClientFeature.resetCourse, desc: "Reset Course", customChecked: accessPermissions.canResetCourse },
    { name: ClientFeature.dashboard, desc: "Access Dashboard", customChecked: accessPermissions.accessDashboard },
  ];

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    const action = checked ? insertClientFeature(name as ClientFeature) : deleteClientFeature(name as ClientFeature);

    action.then(() => {
      const updateAction = checked ? Action.addFeature : Action.removeFeature;
      const updatedFeatures = updateClientFeatures(
        clientFeatures as ClientFeature[],
        name as ClientFeature,
        updateAction
      );
      setClientFeatures(updatedFeatures);
      setModalMessage("Successfully updated account features");
    });
  };

  return (
    <div data-testid="account-features">
      <h2>Account Access</h2>
      <FormContainer>
        {checkboxDescriptions.map(({ name, desc, customChecked }) => (
          <AccountFeatureCheckbox
            customChecked={customChecked}
            description={desc}
            name={name}
            onChange={handleOnChange}
          />
        ))}
      </FormContainer>
    </div>
  );
};

export default AccountFeatureAccess;
